import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as SolidIcons from '@fortawesome/free-solid-svg-icons';
import * as RegularIcons from '@fortawesome/free-regular-svg-icons';

// Combine all icons for lookup
const ALL_FA_ICONS = { ...SolidIcons, ...RegularIcons };

/**
 * Default Campaign Icon - Crossed Swords SVG
 * Used as fallback when a campaign has no icon assigned.
 * Matches the icon used in the sidebar.
 */
export const DefaultCampaignIcon = ({ size = 14 }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: 'inline-block', verticalAlign: 'middle' }}
  >
    {/* Sword 1: bottom-left to top-right */}
    <line x1="4" y1="20" x2="20" y2="4" />
    <polyline points="20,4 20,9 15,9" />
    <line x1="4" y1="20" x2="7" y2="17" />
    <line x1="6" y1="21" x2="3" y2="18" />
    
    {/* Sword 2: bottom-right to top-left */}
    <line x1="20" y1="20" x2="4" y2="4" />
    <polyline points="4,4 4,9 9,9" />
    <line x1="20" y1="20" x2="17" y2="17" />
    <line x1="18" y1="21" x2="21" y2="18" />
  </svg>
);

/**
 * Custom icon image component with error handling
 * Falls back to fallback element if image fails to load (e.g., deleted icon)
 * Calls onError callback to allow parent to clear the broken icon reference
 */
const CustomIconImage = ({ src, alt, size, fallback, onError }) => {
  const [hasError, setHasError] = useState(false);
  
  // Reset error state if src changes
  useEffect(() => {
    setHasError(false);
  }, [src]);
  
  if (hasError) {
    return fallback; // Return fallback if image failed to load
  }
  
  return (
    <img 
      src={src} 
      alt={alt}
      onError={() => {
        setHasError(true);
        // Call onError callback so parent can clear the broken icon
        if (onError) {
          onError();
        }
      }}
      style={{ 
        width: size, 
        height: size, 
        objectFit: 'contain',
        display: 'inline-block',
        verticalAlign: 'middle',
        borderRadius: '2px',
      }} 
    />
  );
};

/**
 * Renders an icon - handles emojis, FontAwesome icons, and custom uploaded icons
 * @param {string|object} icon - Either an emoji string, or an object { type: 'emoji'|'fontawesome'|'custom', value: string, color?: string, publicUrl?: string }
 * @param {object} options - Optional settings { fallback: ReactNode, defaultColor: string, size: string, nodeColor: string, onCustomIconError: function }
 * @returns {React.ReactNode}
 */
export const renderIcon = (icon, options = {}) => {
  const { fallback = null, defaultColor = '#9ca3af', size = '1em', nodeColor, onCustomIconError } = options;
  
  if (!icon) {
    return fallback;
  }
  
  // Handle custom uploaded icon
  if (typeof icon === 'object' && icon.type === 'custom') {
    if (icon.publicUrl) {
      return (
        <CustomIconImage 
          src={icon.publicUrl} 
          alt={icon.fileName || 'Custom icon'}
          size={size}
          fallback={fallback}
          onError={onCustomIconError}
        />
      );
    }
    return fallback;
  }
  
  // Handle FontAwesome icon object
  if (typeof icon === 'object' && icon.type === 'fontawesome') {
    // New format - lookup icon by name from FontAwesome library
    const faIcon = ALL_FA_ICONS[icon.value];
    if (faIcon) {
      // If color is 'inherit', use nodeColor or defaultColor
      const resolvedColor = icon.color === 'inherit' 
        ? (nodeColor || defaultColor) 
        : (icon.color || defaultColor);
      return (
        <FontAwesomeIcon 
          icon={faIcon} 
          style={{ 
            color: resolvedColor, 
            fontSize: size,
            display: 'inline-block',
            verticalAlign: 'middle'
          }} 
        />
      );
    }
    
    // Legacy format with embedded SVG path (for backwards compatibility)
    if (icon.path && icon.viewBox) {
      return (
        <svg 
          width={size} 
          height={size} 
          viewBox={icon.viewBox} 
          fill={icon.color || defaultColor}
          style={{ display: 'inline-block', verticalAlign: 'middle' }}
        >
          <path d={icon.path} />
        </svg>
      );
    }
    
    return fallback;
  }
  
  // Handle emoji object
  if (typeof icon === 'object' && icon.type === 'emoji') {
    return <span style={{ fontSize: size, lineHeight: 1 }}>{icon.value}</span>;
  }
  
  // It's a plain emoji string (legacy support)
  if (typeof icon === 'string') {
    return <span style={{ fontSize: size, lineHeight: 1 }}>{icon}</span>;
  }
  
  return fallback;
};

export default renderIcon;
